import React from 'react';
import { SequenceMissionProps } from '../../../types/mission';
import { SequenceChallengeResult } from '../../chess/SequenceChallenge';
import { getTacticalThemeDescription } from '../../../data';
import styles from './SequenceMissionResults.module.css';
import { formatTime } from '../../../utils/timeUtils';
import {
  formatSkillLabel,
  getScorePerformanceBand,
  getStarRating,
  STAR_COUNT,
} from '../../../utils/missionDisplay';
import { normalizeSequenceMissionResults } from '../../../utils/missionResultsDisplay';

/**
 * Props for the SequenceMissionResults component
 */
interface SequenceMissionResultsProps {
  mission: SequenceMissionProps;
  result: SequenceChallengeResult;
  onContinue: () => void;
  onRetry: () => void;
}

/**
 * SequenceMissionResults component
 * 
 * Displays the results for a completed sequence mission, including performance metrics,
 * rewards, and tactical theme explanations.
 */
const SequenceMissionResults: React.FC<SequenceMissionResultsProps> = ({
  mission,
  result,
  onContinue,
  onRetry,
}) => {
  const normalizedResults = normalizeSequenceMissionResults(result, mission);
  const normalizedScore = normalizedResults.score;
  const roundedTimeSpent = normalizedResults.timeSpent;

  // --- Helper functions ---
  const starCount = getStarRating(normalizedScore);
  const performanceBand = getScorePerformanceBand(normalizedScore);

  /**
   * Render star rating
   */
  const renderStars = () => {
    const stars = [];
    for (let i = 0; i < STAR_COUNT; i++) {
      stars.push(
        <span 
          key={i} 
          className={`${styles.star} ${i < starCount ? styles.filled : ''}`}
          aria-hidden="true"
        >
          ★
        </span>
      );
    }
    return (
      <div className={styles.starsContainer} aria-label={`${starCount} out of ${STAR_COUNT} stars`}>
        {stars}
      </div>
    );
  };
  
  /**
   * Get score class for conditional styling
   */
  const getScoreClass = (): string => {
    if (performanceBand === 'excellent') return styles.excellent;
    if (performanceBand === 'good') return styles.good;
    if (performanceBand === 'average') return styles.average;
    return styles.poor;
  };

  /**
   * Get performance assessment based on score
   */
  const getPerformanceText = (): string => {
    if (performanceBand === 'excellent') return 'Field operations executed with exceptional precision, agent!';
    if (performanceBand === 'good') return 'Tactical execution completed successfully, agent.';
    if (performanceBand === 'average') return 'Mission objectives achieved with minimal efficiency.';
    return 'Operation compromised. Tactical review required.';
  };

  /**
   * Render tactical theme explanation
   */
  const renderTacticalExplanation = () => {
    const themeDescription = getTacticalThemeDescription(mission.tacticalTheme);
    
    return (
      <div className={styles.tacticalExplanation}>
        <h2 className={styles.sectionTitle}>
          Intelligence Report: {mission.tacticalTheme.charAt(0).toUpperCase() + mission.tacticalTheme.slice(1)}
        </h2>
        <p className={styles.explanationText}>{themeDescription}</p>
        
        {result.success && (
          <div className={styles.congratulations}>
            <p>Mission accomplished! Your execution of the {mission.tacticalTheme} tactic was successful.</p>
            <p>Add this tactical pattern to your operational arsenal for future missions.</p>
          </div>
        )}
        
        {!result.success && (
          <div className={styles.encouragement}>
            <p>Mission status: Incomplete. Further training on the {mission.tacticalTheme} pattern is recommended.</p>
            <p>Review operational details and attempt re-execution of the sequence.</p>
          </div>
        )}
      </div>
    );
  };

  // Calculate earned rewards (only if successful)
  const earnedExperience = result.success ? mission.rewards.experience : Math.round(mission.rewards.experience * 0.25);
  const earnedSkillPoints = {
    coordinates: result.success ? mission.rewards.skillPoints.coordinates : 0,
    basicMoves: result.success ? mission.rewards.skillPoints.basicMoves : 0,
    sequences: result.success ? mission.rewards.skillPoints.sequences : Math.round(mission.rewards.skillPoints.sequences * 0.25),
    gameAnalysis: result.success ? mission.rewards.skillPoints.gameAnalysis : 0,
  };

  const formattedTime = formatTime(roundedTimeSpent);
  const completedMoves = normalizedResults.completedMoves;
  const totalMoves = normalizedResults.totalMoves;
  const completionRate = normalizedResults.completionRate;

  return (
    <div className={styles.container} data-testid="sequence-mission-results">
      <div className={styles.header}>
        <h1 className={`${styles.resultsTitle} ${result.success ? styles.success : styles.failure}`}>
          {result.success ? 'Mission Accomplished' : 'Mission Failed'}
        </h1>
      </div>

      <div className={styles.missionInfo}>
        <h2 className={styles.missionTitle}>{mission.title}</h2>
        <p className={styles.missionObjective}>{mission.objective}</p>
      </div>

      <div className={styles.resultsOverview}>
        <div className={styles.scoreSection}>
          <h2 className={styles.sectionTitle}>Performance</h2>
          <div className={styles.scoreDisplay}>
            <div className={`${styles.scoreValue} ${getScoreClass()}`}>
              {normalizedScore}%
            </div>
            <div className={styles.scoreLabel}>Mission Score</div>
          </div>
          {renderStars()}
          <p className={styles.performanceText}>{getPerformanceText()}</p>
        </div>

        <div className={styles.statsSection}>
          <h2 className={styles.sectionTitle}>Mission Statistics</h2>
          <div className={styles.statsGrid}>
            <div className={styles.statItem}>
              <div className={styles.statIcon}>⏱️</div>
              <div className={styles.statLabel}>Time</div>
              <div className={styles.statValue}>{formattedTime}</div>
            </div>
            
            <div className={styles.statItem}>
              <div className={styles.statIcon}>♟️</div>
              <div className={styles.statLabel}>Moves</div>
              <div className={styles.statValue}>{completedMoves}/{totalMoves}</div>
            </div>

            <div className={styles.statItem}>
              <div className={styles.statIcon}>📊</div>
              <div className={styles.statLabel}>Completion</div>
              <div className={styles.statValue}>{completionRate}%</div>
            </div>
            
            <div className={styles.statItem}>
              <div className={styles.statIcon}>🎮</div>
              <div className={styles.statLabel}>Status</div>
              <div className={`${styles.statValue} ${result.success ? styles.success : styles.failure}`}>
                {result.success ? 'Success' : 'Failed'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {renderTacticalExplanation()}

      <div className={styles.rewardsSection}>
        <h2 className={styles.sectionTitle}>Rewards Earned</h2>
        <div className={styles.rewardsGrid}>
          <div className={styles.rewardItem}>
            <div className={styles.rewardIcon}>🎖️</div>
            <div className={styles.rewardLabel}>Experience</div>
            <div className={styles.rewardValue}>
              {earnedExperience} XP
              {!result.success && <span className={styles.partialReward}> (partial)</span>}
            </div>
          </div>
          
          {Object.entries(earnedSkillPoints).map(([skill, points]) => (
            points > 0 && (
              <div key={skill} className={styles.rewardItem}>
                <div className={styles.rewardIcon}>🔺</div>
                <div className={styles.rewardLabel}>
                  {formatSkillLabel(skill)}
                </div>
                <div className={styles.rewardValue}>
                  +{points} points
                  {!result.success && <span className={styles.partialReward}> (partial)</span>}
                </div>
              </div>
            )
          ))}
        </div>
      </div>

      <div className={styles.actionsSection}>
        <button 
          className={styles.continueButton}
          onClick={onContinue}
          data-testid="continue-button"
        >
          Continue
        </button>
        <button 
          className={styles.retryButton}
          onClick={onRetry}
          data-testid="retry-button"
        >
          Retry Mission
        </button>
      </div>
    </div>
  );
};

export default SequenceMissionResults; 
